import { BadRequestException, Injectable, Optional } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { MediaUploadService } from '../media/media-upload.service';
import { StorefrontBranding, StorefrontSocialLink } from '@cart/shared-types';
import { ResolvedBranding, storeFaqs, whatsappLink } from '@cart/shared-utils';

/** Logo roles, in the order a UI should offer them. */
export const LOGO_ROLES = ['logo', 'logoDark', 'logoTransparent', 'favicon', 'ogImage'] as const;
export type LogoRole = (typeof LOGO_ROLES)[number];

export const ROBOTS_POLICIES = [
  'index,follow',
  'index,nofollow',
  'noindex,follow',
  'noindex,nofollow',
] as const;

/**
 * Branding and SEO, across the two tiers.
 *
 * **Merchant** is the business — one legal entity, one logo, one set of social
 * profiles. It feeds invoices, transactional email and `Organization` JSON-LD,
 * and is the default for every store beneath it.
 *
 * **Store** is a shopfront. Search SEO lives here because a store is what has a
 * URL, and so does the local block — area, address, coordinates, hours — which
 * is what actually puts a shop in a map pack for its own town. "Grocery store
 * in Karimnagar" is answered by `LocalBusiness` markup carrying a real address,
 * not by the word Karimnagar in a meta tag.
 *
 * The precedence rule lives here and nowhere else. Two callers implementing
 * "store value, else merchant value" separately is two chances to disagree
 * about it, and the disagreement would show up as a storefront rendering one
 * logo while the invoice carries another.
 */
@Injectable()
export class BrandingService {
  constructor(
    private readonly prisma: PrismaService,
    // Optional for the same reason CommerceAdminService takes it optionally:
    // the media stack is not wired in every environment, and branding without
    // logo upload is still worth having.
    @Optional() private readonly mediaUpload?: MediaUploadService
  ) {}

  async getMerchantBranding(organizationId: string) {
    return (
      (await this.prisma.merchantBranding.findUnique({ where: { organizationId } })) ??
      // Never null: the form should render empty fields rather than 404, and
      // callers should never have to distinguish "no row yet" from "nothing set".
      this.emptyMerchant(organizationId)
    );
  }

  async saveMerchantBranding(
    organizationId: string,
    data: Record<string, unknown>,
    actorId: string
  ) {
    const clean = this.sanitise(data, MERCHANT_FIELDS);
    return this.prisma.merchantBranding.upsert({
      where: { organizationId },
      create: { organizationId, ...clean, createdBy: actorId, updatedBy: actorId },
      update: { ...clean, updatedBy: actorId },
    });
  }

  async getStoreBranding(storeId: string) {
    return (
      (await this.prisma.storeBranding.findUnique({ where: { storeId } })) ??
      this.emptyStore(storeId)
    );
  }

  async saveStoreBranding(storeId: string, data: Record<string, unknown>, actorId: string) {
    const clean = this.sanitise(data, STORE_FIELDS);

    const saved = await this.prisma.storeBranding.upsert({
      where: { storeId },
      create: { storeId, ...clean, createdBy: actorId, updatedBy: actorId },
      update: { ...clean, updatedBy: actorId },
    });

    await this.syncStoreAddress(storeId, clean, actorId);
    return saved;
  }

  /**
   * Keep the store's own city and state in step with the address typed here.
   *
   * These are the same fact stored twice, and the copy that mattered was the
   * one nobody was filling in. `Store.state` is what decides CGST+SGST versus
   * IGST at checkout and what resolves the courier zone; `store_branding` is
   * only what gets published. A merchant who entered their address on the SEO
   * tab therefore had a shop that looked complete, read as "no address" on the
   * setup checklist, and quietly charged GST as though every order were
   * intra-state.
   *
   * Writing through rather than teaching every reader to fall back: a fallback
   * would leave two values free to disagree, and the disagreement is invisible
   * until it shows up on a tax return.
   */
  private async syncStoreAddress(
    storeId: string,
    clean: Record<string, unknown>,
    actorId: string
  ): Promise<void> {
    const city = typeof clean['locality'] === 'string' ? clean['locality'] : null;
    const state = typeof clean['region'] === 'string' ? clean['region'] : null;
    if (!city && !state) return;

    await this.prisma.store.update({
      where: { id: storeId },
      data: {
        ...(city ? { city } : {}),
        ...(state ? { state } : {}),
        updatedBy: actorId,
      },
    }).catch(() => undefined);
  }

  /**
   * What a store actually renders with: its own values, falling back to the
   * merchant's.
   *
   * The one function the storefront, the invoice renderer and the email
   * renderer should all call. `inherited` names which fields came from the
   * merchant, so the admin can grey them in the form and a merchant can see at
   * a glance what is shared and what is this shop's own.
   */
  async resolveForStore(storeId: string) {
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: { id: true, name: true, organizationId: true, city: true, state: true, country: true },
    });
    if (!store) return null;

    const [merchant, branding] = await Promise.all([
      this.prisma.merchantBranding.findUnique({ where: { organizationId: store.organizationId } }),
      this.prisma.storeBranding.findUnique({ where: { storeId } }),
    ]);

    const inherited: string[] = [];
    const pick = <T>(own: T | null | undefined, parent: T | null | undefined, field: string): T | null => {
      if (own !== null && own !== undefined && own !== '') return own;
      if (parent !== null && parent !== undefined && parent !== '') {
        inherited.push(field);
        return parent;
      }
      return null;
    };

    return {
      storeId: store.id,
      storeName: store.name,
      organizationId: store.organizationId,

      logoAssetId: pick(branding?.logoAssetId, merchant?.logoAssetId, 'logoAssetId'),
      logoDarkAssetId: pick(branding?.logoDarkAssetId, merchant?.logoDarkAssetId, 'logoDarkAssetId'),
      logoTransparentAssetId: pick(
        branding?.logoTransparentAssetId, merchant?.logoTransparentAssetId, 'logoTransparentAssetId'
      ),
      faviconAssetId: pick(branding?.faviconAssetId, merchant?.faviconAssetId, 'faviconAssetId'),
      // No merchant equivalent: a share card is per shopfront by nature.
      ogImageAssetId: branding?.ogImageAssetId ?? null,

      metaTitle: pick(branding?.metaTitle, merchant?.metaTitle, 'metaTitle'),
      metaDescription: pick(branding?.metaDescription, merchant?.metaDescription, 'metaDescription'),
      metaKeywords: pick(branding?.metaKeywords, merchant?.metaKeywords, 'metaKeywords'),
      canonicalBaseUrl: branding?.canonicalBaseUrl ?? null,
      robotsPolicy: branding?.robotsPolicy ?? 'index,follow',

      // Local. Deliberately not inherited: a Karimnagar address on a Jagityal
      // shop would be worse than no address, because it would be believed.
      businessType: branding?.businessType ?? null,
      additionalTypes: branding?.additionalTypes ?? null,
      priceRange: branding?.priceRange ?? null,
      googlePlaceId: branding?.googlePlaceId ?? null,
      areaName: branding?.areaName ?? null,
      streetAddress: branding?.streetAddress ?? null,
      locality: branding?.locality ?? store.city ?? null,
      region: branding?.region ?? store.state ?? null,
      postalCode: branding?.postalCode ?? null,
      countryCode: branding?.countryCode ?? store.country ?? 'IN',
      latitude: branding?.latitude ? Number(branding.latitude) : null,
      longitude: branding?.longitude ? Number(branding.longitude) : null,
      serviceAreas: branding?.serviceAreas ?? null,
      serviceRadiusKm: branding?.serviceRadiusKm ?? null,
      openingHours: branding?.openingHours ?? null,
      specialHours: branding?.specialHours ?? null,
      productTitleTemplate: branding?.productTitleTemplate ?? null,
      faqs: branding?.faqs ?? null,
      allowAiCrawlers: branding?.allowAiCrawlers ?? true,
      returnWindowDays: branding?.returnWindowDays ?? null,
      updatedAt: branding?.updatedAt ?? null,
      contactPhone: pick(branding?.contactPhone, merchant?.supportPhone, 'contactPhone'),
      contactEmail: pick(branding?.contactEmail, merchant?.supportEmail, 'contactEmail'),
      whatsappNumber: pick(branding?.whatsappNumber, merchant?.whatsappNumber, 'whatsappNumber'),
      mapsUrl: branding?.mapsUrl ?? null,

      legalName: merchant?.legalName ?? null,
      social: {
        twitter: merchant?.twitterHandle ?? null,
        facebook: merchant?.facebookUrl ?? null,
        instagram: merchant?.instagramUrl ?? null,
        linkedin: merchant?.linkedinUrl ?? null,
        youtube: merchant?.youtubeUrl ?? null,
      },

      googleSiteVerification: branding?.googleSiteVerification ?? null,
      bingSiteVerification: branding?.bingSiteVerification ?? null,
      gaMeasurementId: branding?.gaMeasurementId ?? null,
      gtmContainerId: branding?.gtmContainerId ?? null,

      inherited,
    };
  }

  /**
   * How complete this store's SEO is, and what is missing.
   *
   * An unset optional field looks exactly like a considered one, so without
   * this a merchant has no way to know they never wrote a description. The
   * weights are not arbitrary — a missing description costs more than a missing
   * Twitter handle because one changes what a search result says and the other
   * does not.
   */
  async completeness(storeId: string) {
    const resolved = await this.resolveForStore(storeId);
    if (!resolved) return null;

    const checks: { field: string; label: string; weight: number; done: boolean }[] = [
      { field: 'metaTitle', label: 'Page title', weight: 3, done: !!resolved.metaTitle },
      { field: 'metaDescription', label: 'Meta description', weight: 3, done: !!resolved.metaDescription },
      { field: 'logoAssetId', label: 'Logo', weight: 2, done: !!resolved.logoAssetId },
      { field: 'ogImageAssetId', label: 'Social share image', weight: 2, done: !!resolved.ogImageAssetId },
      { field: 'areaName', label: 'Area served', weight: 2, done: !!resolved.areaName },
      { field: 'streetAddress', label: 'Street address', weight: 2, done: !!resolved.streetAddress },
      { field: 'latitude', label: 'Map coordinates', weight: 1, done: resolved.latitude !== null },
      { field: 'openingHours', label: 'Opening hours', weight: 1, done: !!resolved.openingHours },
      { field: 'contactPhone', label: 'Contact phone', weight: 1, done: !!resolved.contactPhone },
      { field: 'businessType', label: 'Business type', weight: 1, done: !!resolved.businessType },
      // The one field that ties this store to its Google listing, so it is
      // worth as much as the address itself.
      { field: 'googlePlaceId', label: 'Google Business Profile', weight: 2, done: !!resolved.googlePlaceId },
      { field: 'priceRange', label: 'Price range', weight: 1, done: !!resolved.priceRange },
    ];

    const total = checks.reduce((n, c) => n + c.weight, 0);
    const earned = checks.filter((c) => c.done).reduce((n, c) => n + c.weight, 0);

    return {
      percent: Math.round((earned / total) * 100),
      missing: checks.filter((c) => !c.done).map((c) => ({ field: c.field, label: c.label })),
      /*
       * LocalBusiness markup is emitted only when the address is real. A
       * partial address produces structured data a search engine will believe
       * and act on, which is worse than emitting none.
       */
      localBusinessReady: Boolean(
        resolved.streetAddress && resolved.locality && resolved.postalCode
      ),
    };
  }

  /**
   * Store a logo and point the branding row at it.
   *
   * Goes through `uploadProxied` like every other image, which is what makes it
   * count against the merchant's quota, land on whichever backend their account
   * is currently on, and appear in the media trash when replaced. A bespoke
   * upload path here would have quietly bypassed all three.
   *
   * `storeId` null means the merchant tier: one logo, inherited by every store.
   */
  async uploadLogo(args: {
    organizationId: string;
    storeId: string | null;
    role: LogoRole;
    file: { buffer: Buffer; originalname: string; mimetype: string };
    actorId: string;
  }): Promise<{ role: LogoRole; assetId: string; url: string | null; quotaWarning: boolean }> {
    if (!LOGO_ROLES.includes(args.role)) {
      throw new BadRequestException(`Unknown logo role "${args.role}"`);
    }
    if (!args.file?.buffer?.length) {
      throw new BadRequestException('file is required');
    }
    if (!this.mediaUpload) {
      throw new BadRequestException('Media storage is not configured on this environment');
    }

    const asset = await this.mediaUpload.uploadProxied({
      organizationId: args.organizationId,
      storeId: args.storeId ?? undefined,
      ownerType: 'logo',
      ownerId: args.storeId ?? args.organizationId,
      fileName: args.file.originalname,
      declaredMimeType: args.file.mimetype,
      buffer: args.file.buffer,
      // Logos are line art and flat colour far more often than photographs;
      // lossy recompression on a small PNG mark makes it visibly worse while
      // saving almost nothing.
      compressionPercent: 0,
    });

    await this.writeLogoField(args.organizationId, args.storeId, args.role, asset.id, args.actorId);

    return {
      role: args.role,
      assetId: asset.id,
      url: asset.publicUrl ?? null,
      quotaWarning: asset.quotaWarning,
    };
  }

  /**
   * Clear a logo slot.
   *
   * The asset itself is left alone rather than deleted: it may still be
   * referenced by another store or role, and the media trash sweep is what owns
   * reclaiming space. Unsetting the pointer is the whole of this operation.
   */
  async clearLogo(
    organizationId: string,
    storeId: string | null,
    role: LogoRole,
    actorId: string
  ) {
    if (!LOGO_ROLES.includes(role)) {
      throw new BadRequestException(`Unknown logo role "${role}"`);
    }
    return this.writeLogoField(organizationId, storeId, role, null, actorId);
  }

  /**
   * Set one logo column, bypassing {@link sanitise}.
   *
   * These columns are deliberately absent from MERCHANT_FIELDS / STORE_FIELDS,
   * so the general PATCH cannot reach them — see the note on those lists. This
   * is the only writer, which is what makes that guarantee hold.
   */
  private async writeLogoField(
    organizationId: string,
    storeId: string | null,
    role: LogoRole,
    assetId: string | null,
    actorId: string
  ) {
    const data = { [`${role}AssetId`]: assetId };

    if (storeId) {
      return this.prisma.storeBranding.upsert({
        where: { storeId },
        create: { storeId, ...data, createdBy: actorId, updatedBy: actorId },
        update: { ...data, updatedBy: actorId },
      });
    }

    return this.prisma.merchantBranding.upsert({
      where: { organizationId },
      create: { organizationId, ...data, createdBy: actorId, updatedBy: actorId },
      update: { ...data, updatedBy: actorId },
    });
  }

  /**
   * The URLs behind whatever logo slots are set, for the admin form.
   *
   * The form holds asset *ids*; showing a merchant an id is showing them
   * nothing, so the modal needs the URLs to render a thumbnail beside each
   * slot.
   */
  async logoUrls(organizationId: string, storeId: string | null) {
    // The two tiers hold different column sets — a merchant has no ogImage,
    // because a share card is per shopfront by nature — so this reads through a
    // plain record and simply finds nothing where a slot does not exist.
    const record = (storeId
      ? await this.getStoreBranding(storeId)
      : await this.getMerchantBranding(organizationId)) as unknown as Record<string, unknown>;

    const idFor = (role: LogoRole) => (record[`${role}AssetId`] as string | null) ?? null;
    const urls = await this.assetUrls(LOGO_ROLES.map(idFor));

    return Object.fromEntries(
      LOGO_ROLES.map((role) => {
        const id = idFor(role);
        return [role, id ? urls.get(id) ?? null : null];
      })
    ) as Record<LogoRole, string | null>;
  }

  /**
   * What the storefront renders in `<head>` and in its footer.
   *
   * Deliberately a different shape from {@link resolveForStore}: that one
   * serves the admin form and names what is inherited, this one serves a public
   * page and has already made every decision. Asset ids become URLs, a
   * WhatsApp number becomes a click-to-chat link, and an address too partial to
   * publish becomes null rather than a half-address a visitor might drive to.
   */
  async storefrontBranding(storeId: string): Promise<StorefrontBranding | null> {
    const resolved = await this.resolveForStore(storeId);
    if (!resolved) return null;

    /*
     * The free-delivery figure is DERIVED, never stored on branding.
     *
     * It lives on the shipping channels, where it belongs — free shipping is
     * per channel, and one branding column could not say "free above ₹499 by
     * rider, ₹999 by courier". Taking the lowest active threshold is the honest
     * thing to advertise: it is the one a shopper can definitely reach. See
     * docs/25-india-shipping-redesign.md §8.
     */
    const thresholds = await this.prisma.shippingChannel.findMany({
      where: { storeId, isActive: true, freeAbovePaise: { not: null } },
      select: { freeAbovePaise: true },
    });
    const freeDeliveryOverPaise = thresholds.length
      ? Math.min(...thresholds.map((t) => t.freeAbovePaise as number))
      : null;

    const urls = await this.assetUrls([
      resolved.logoAssetId,
      resolved.faviconAssetId,
      resolved.ogImageAssetId,
    ]);

    const address =
      resolved.streetAddress && resolved.locality && resolved.postalCode
        ? {
            streetAddress: resolved.streetAddress,
            locality: resolved.locality,
            region: resolved.region,
            postalCode: resolved.postalCode,
            countryCode: resolved.countryCode,
          }
        : null;

    const hours = Array.isArray(resolved.openingHours)
      ? (resolved.openingHours as { day: string; opens?: string; closes?: string; closed?: boolean }[])
          .filter((h) => h?.day && !h.closed && h.opens && h.closes)
          .map((h) => ({ day: h.day, opens: h.opens as string, closes: h.closes as string }))
      : [];

    return {
      metaTitle: resolved.metaTitle,
      metaDescription: resolved.metaDescription,
      metaKeywords: resolved.metaKeywords,
      canonicalBaseUrl: resolved.canonicalBaseUrl,
      robotsPolicy: resolved.robotsPolicy,

      logoUrl: urls.get(resolved.logoAssetId ?? '') ?? null,
      faviconUrl: urls.get(resolved.faviconAssetId ?? '') ?? null,
      ogImageUrl: urls.get(resolved.ogImageAssetId ?? '') ?? null,

      areaName: resolved.areaName,
      contactPhone: resolved.contactPhone,
      contactEmail: resolved.contactEmail,
      whatsappUrl: whatsappLink(resolved.whatsappNumber),
      mapsUrl: resolved.mapsUrl,
      social: socialLinks(resolved.social, resolved.whatsappNumber),

      productTitleTemplate: resolved.productTitleTemplate,

      googleSiteVerification: resolved.googleSiteVerification,
      bingSiteVerification: resolved.bingSiteVerification,
      gaMeasurementId: resolved.gaMeasurementId,
      gtmContainerId: resolved.gtmContainerId,

      address,
      openingHours: hours,

      // Generated here rather than in the storefront so the answers are
      // identical in the HTML, the JSON-LD and anywhere else that needs them —
      // an engine quoting one and reading the other must not find them
      // disagreeing about when the shop shuts.
      faqs: storeFaqs(
        {
          storeName: resolved.storeName,
          areaName: resolved.areaName,
          streetAddress: resolved.streetAddress,
          locality: resolved.locality,
          region: resolved.region,
          postalCode: resolved.postalCode,
          serviceAreas: resolved.serviceAreas,
          serviceRadiusKm: resolved.serviceRadiusKm,
          openingHours: hours,
          contactPhone: resolved.contactPhone,
          contactEmail: resolved.contactEmail,
          whatsappUrl: whatsappLink(resolved.whatsappNumber),
          returnWindowDays: resolved.returnWindowDays,
          freeDeliveryOverPaise,
          mapsUrl: resolved.mapsUrl,
        },
        resolved.faqs
      ),
      allowAiCrawlers: resolved.allowAiCrawlers,
      returnWindowDays: resolved.returnWindowDays,
      freeDeliveryOverPaise,
      updatedAt: resolved.updatedAt ? new Date(resolved.updatedAt).toISOString() : null,

      jsonLdInput: {
        storeName: resolved.storeName,
        legalName: resolved.legalName,
        businessType: resolved.businessType,
        additionalTypes: resolved.additionalTypes,
        priceRange: resolved.priceRange,
        googlePlaceId: resolved.googlePlaceId,
        whatsappNumber: resolved.whatsappNumber,
        serviceRadiusKm: resolved.serviceRadiusKm,
        specialHours: resolved.specialHours,
        areaName: resolved.areaName,
        streetAddress: resolved.streetAddress,
        locality: resolved.locality,
        region: resolved.region,
        postalCode: resolved.postalCode,
        countryCode: resolved.countryCode,
        latitude: resolved.latitude,
        longitude: resolved.longitude,
        serviceAreas: resolved.serviceAreas,
        openingHours: resolved.openingHours,
        contactPhone: resolved.contactPhone,
        contactEmail: resolved.contactEmail,
        mapsUrl: resolved.mapsUrl,
        social: resolved.social,
      } satisfies ResolvedBranding,
    };
  }

  /**
   * Asset id → public URL, in one query.
   *
   * Only public URLs: a logo behind a signed URL would 404 for every visitor
   * the moment the signature expired, which is worse than no logo because the
   * page would render a broken image on every request thereafter.
   */
  private async assetUrls(ids: (string | null)[]): Promise<Map<string, string>> {
    const wanted = ids.filter((id): id is string => !!id);
    if (!wanted.length) return new Map();

    const assets = await this.prisma.mediaAsset.findMany({
      where: { id: { in: wanted }, publicUrl: { not: null } },
      select: { id: true, publicUrl: true },
    });
    return new Map(assets.map((a) => [a.id, a.publicUrl as string]));
  }

  /**
   * Only known columns, trimmed, with empty strings normalised to null.
   *
   * Except for the columns that cannot hold null. `robotsPolicy` and
   * `countryCode` are `NOT NULL` with defaults, and a form that submits them
   * empty would otherwise send an explicit null — which Prisma cannot match
   * against the unchecked input at all, so it falls back to reporting the
   * *checked* variant and the whole save fails with the thoroughly misleading
   * "Argument `store` is missing". Omitting the key instead keeps the column's
   * default on create and its current value on update, which is what an
   * untouched field should do in both cases.
   */
  private sanitise(data: Record<string, unknown>, allowed: readonly string[]) {
    const out: Record<string, unknown> = {};
    for (const key of allowed) {
      if (!(key in data)) continue;

      const raw = data[key];
      const value = typeof raw === 'string' ? (raw.trim() || null) : raw ?? null;

      if (value === null && NON_NULLABLE.has(key)) continue;
      out[key] = value;
    }
    return out;
  }

  private emptyMerchant(organizationId: string) {
    return Object.fromEntries([
      ['organizationId', organizationId],
      ...MERCHANT_FIELDS.map((f) => [f, null]),
      // Not writable through the form, but still part of the record a caller
      // reads back.
      ...LOGO_ROLES.map((role) => [`${role}AssetId`, null]),
    ]) as Record<string, unknown>;
  }

  /**
   * The shape of a store that has never been saved.
   *
   * Defaults go **after** the nulls, not before: `Object.fromEntries` keeps the
   * last entry for a repeated key, so listing them first meant every column in
   * STORE_FIELDS — `robotsPolicy` and `countryCode` among them — came back
   * null. That reached the admin form as an unselected dropdown and came
   * straight back as an explicit null on save, which is a NOT NULL column and a
   * 500. The order here is load-bearing.
   */
  private emptyStore(storeId: string) {
    return Object.fromEntries([
      ['storeId', storeId],
      ...STORE_FIELDS.map((f) => [f, null]),
      ...LOGO_ROLES.map((role) => [`${role}AssetId`, null]),
      ['robotsPolicy', 'index,follow'],
      ['countryCode', 'IN'],
    ]) as Record<string, unknown>;
  }
}

/*
 * Note what is NOT here: the `*AssetId` columns.
 *
 * A logo is uploaded through its own endpoint, which writes the column
 * directly. If these lists carried the asset ids too, the form's PATCH would
 * also write them — and the form holds whatever it loaded when it opened, so
 * saving after an upload would put the *old* value (usually null) back over the
 * logo that had just been stored. That is exactly what happened: three logos
 * sat in R2, consuming quota, with nothing pointing at them.
 *
 * Leaving them out makes the upload endpoint the single writer, which is a
 * guarantee rather than a convention a future caller has to remember.
 */
const MERCHANT_FIELDS = [
  'legalName', 'supportEmail', 'supportPhone', 'whatsappNumber',
  'metaTitle', 'metaDescription', 'metaKeywords',
  'twitterHandle', 'facebookUrl', 'instagramUrl', 'linkedinUrl', 'youtubeUrl',
] as const;

/**
 * Columns declared `NOT NULL` with a database default.
 *
 * Kept as an explicit list rather than inferred, because the failure mode when
 * one is missing is a 500 that names a completely unrelated field — the cost of
 * getting this wrong is far higher than the cost of writing it down.
 */
const NON_NULLABLE = new Set(['robotsPolicy', 'countryCode', 'allowAiCrawlers']);

// Asset ids are absent here for the same reason — see MERCHANT_FIELDS above.
const STORE_FIELDS = [
  'metaTitle', 'metaDescription', 'metaKeywords', 'canonicalBaseUrl', 'robotsPolicy',
  'businessType', 'additionalTypes', 'priceRange', 'googlePlaceId',
  'areaName', 'streetAddress', 'locality', 'region', 'postalCode',
  'countryCode', 'latitude', 'longitude', 'serviceAreas', 'serviceRadiusKm',
  'openingHours', 'specialHours', 'productTitleTemplate',
  'faqs', 'allowAiCrawlers', 'returnWindowDays',
  'contactPhone', 'contactEmail', 'whatsappNumber', 'mapsUrl',
  'googleSiteVerification', 'bingSiteVerification', 'gaMeasurementId', 'gtmContainerId',
] as const;

/**
 * Social profiles as footer-ready links.
 *
 * Twitter is stored as a handle and everything else as a URL, which is the
 * shape merchants actually type; normalising it here means the storefront
 * renders one uniform list rather than special-casing one network in a
 * template.
 */
function socialLinks(
  social: Record<string, string | null>,
  whatsappNumber: string | null
): StorefrontSocialLink[] {
  const links: StorefrontSocialLink[] = [];

  const add = (network: StorefrontSocialLink['network'], label: string, url: string | null) => {
    if (url) links.push({ network, label, url });
  };

  const handle = social['twitter']?.trim();
  add(
    'twitter',
    'X',
    handle ? (handle.startsWith('http') ? handle : `https://x.com/${handle.replace(/^@/, '')}`) : null
  );
  add('facebook', 'Facebook', social['facebook']);
  add('instagram', 'Instagram', social['instagram']);
  add('linkedin', 'LinkedIn', social['linkedin']);
  add('youtube', 'YouTube', social['youtube']);
  // Not a profile, but it belongs in the same row: for a great deal of Indian
  // local retail it is the contact channel people actually use.
  add('whatsapp', 'WhatsApp', whatsappLink(whatsappNumber));

  return links;
}
